import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { concat, defer, Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import produce from 'immer';
import { OrderDetails, mapOrderDetails } from '../../models';
import { ApiService, SentryService } from '../../services';
import * as _ from 'lodash';
import { FetchOrdersAndReorders } from './orders.actions';
import { OrdersState, OrdersStateModel } from './orders.state';
import { ApiStoreData, defaultApiStoreData, CcsSentryError, MissingApiRequestDataError } from '../utils';
import { FetchOrder } from './order.actions';
import { PatientState } from '../patient/patient.state';

export type OrderStateModel = ApiStoreData<OrderDetails | null>;

@State<OrderStateModel>({
  name: 'order',
  defaults: defaultApiStoreData,
})
@Injectable({ providedIn: 'root' })
export class OrderState {
  constructor(private apiService: ApiService, private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static order(state: OrderStateModel): OrderDetails | null {
    return state.data;
  }

  @Selector([OrdersState])
  static isLoading(state: OrderStateModel, ordersState: OrdersStateModel): boolean {
    return state.isLoading || ordersState.isLoading;
  }

  @Selector([OrdersState])
  static error(state: OrderStateModel, ordersState: OrdersStateModel): CcsSentryError | null {
    return state.error || ordersState.error;
  }

  getOrder(ctx: StateContext<OrderStateModel>, orderId: number) {
    ctx.setState(
      produce((draft) => {
        draft.isLoading = true;
      })
    );

    let errorMessage = null;

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const patientOrders = this.store.selectSnapshot(OrdersState.allOrders);
    const foundPatientOrder = patientOrders.find((o) => o.id === orderId);

    if (!patientId) {
      errorMessage = 'Missing patient id';
    } else if (!patientOrders || !patientOrders.length) {
      errorMessage = 'Missing patient orders';
    } else if (!foundPatientOrder) {
      errorMessage = `Order ${orderId} not found in patient orders`;
    }

    if (errorMessage) {
      return this.handleError({
        ctx,
        error: new MissingApiRequestDataError([errorMessage]),
        stateKey: 'reorder',
      });
    }

    return this.apiService.getOrderDetails(patientId!, orderId).pipe(
      tap({
        next: (response) => {
          const data = mapOrderDetails(response.data, foundPatientOrder!);
          this.handleSuccess({ ctx, data, stateKey: null });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: null }))
    );
  }

  @Action(FetchOrder)
  fetchOrder(ctx: StateContext<OrderStateModel>, action: FetchOrder) {
    return concat(ctx.dispatch(new FetchOrdersAndReorders()), defer(this.getOrder.bind(this, ctx, action.orderId)));
  }
}
